import { useContext } from 'react';
import Scrollbar from 'src/components/Scrollbar';
import { SidebarContext } from 'src/contexts/SidebarContext';
import { Box, Drawer, alpha, styled, Divider, useTheme, Tooltip } from '@mui/material';
import MenuBookOutlinedIcon from '@mui/icons-material/MenuBookOutlined';
import SidebarMenu from './SidebarMenu';
import Logo from 'src/components/LogoSign';
import getEnvVars from 'src/config/env';

// SidebarWrapper styled component
const SidebarWrapper = styled(Box)(
  ({ theme }) => `
        width: ${theme.spacing(30)}; // Sidebar width with spacing
        min-width: ${theme.spacing(30)};
        color: ${theme.palette.text.primary}; // Use theme's primary text color
        position: relative;
        z-index: 7;
        height: 100%;
        padding-bottom: 68px;
`
);

function Sidebar({ IsDashboard }) {
  const { sidebarToggle, toggleSidebar } = useContext(SidebarContext);
  const closeSidebar = () => toggleSidebar();
  const theme = useTheme();

  // Get version from environment variable or fallback to package.json version
  // If REACT_APP_VERSION contains the placeholder, use the fallback
  const envVersion = process.env.REACT_APP_VERSION;
  const appVersion = (envVersion && !envVersion.includes('%npm_package_version%'))
    ? envVersion
    : '2.0.0'; // Fallback to package.json version

  const { apiUrl } = getEnvVars();
  const handleDownloadUserGuide = () => {
    const link = document.createElement('a');
    link.href = `${apiUrl}/user-guide`;
    link.setAttribute('download', 'Pet-License-User-Guide.docx');
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <>
      {/* Static Sidebar */}
      {IsDashboard !== 1 && <SidebarWrapper
        sx={{
          display: {
            xs: 'none',
            lg: 'inline-block'
          },
          position: 'fixed',
          left: 0,
          top: 0,
          background: alpha(theme.palette.background.paper, 0.95), // Updated for background color
          color: theme.palette.text.primary, // Use theme's primary text color
          boxShadow: theme.palette.mode === 'dark' ? theme.shadows[4] : 'none' // Updated for box shadow
        }}
      >
        <Scrollbar>
          <Box mt={1}>
            <Box
              mx={2}
              sx={{
                height: '57px'
              }}
            >
              <Logo />
            </Box>
          </Box>
          <Divider
            sx={{
              mt: '7px',
              mx: theme.spacing(2)
            }}
          />
          <SidebarMenu />
        </Scrollbar>
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            justifyContent: "center",
            alignItems: "center",
            padding: "8px 20px 24px 20px",
            gap: "8px"
          }}
        >
          <Tooltip title="Download User Guide" arrow>
            <Box
              onClick={handleDownloadUserGuide}
              role="button"
              tabIndex={0}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') handleDownloadUserGuide();
              }}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                cursor: 'pointer',
                color: '#1976d2',
                fontSize: '14px',
                fontWeight: 600,
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              <MenuBookOutlinedIcon fontSize="small" />
              <span>User Guide</span>
            </Box>
          </Tooltip>
          <Box sx={{ fontSize: '11px', fontWeight: 400, color: '#999' }}>
            Version {appVersion}
          </Box>
        </Box>

      </SidebarWrapper>}

      {/* Drawer Sidebar */}
      <Drawer
        sx={{
          boxShadow: theme.shadows[4] 
        }}
        anchor={theme.direction === 'rtl' ? 'right' : 'left'}
        open={sidebarToggle}
        onClose={closeSidebar}
        variant="temporary"
        elevation={9}
      >
        <SidebarWrapper
          sx={{
            background: alpha(theme.palette.background.paper, 0.95), 
            color: theme.palette.text.primary 
          }}
        >
          <Scrollbar>
            <Box mt={1}>
              <Box
                mx={2}
                sx={{
                  height: '57px'
                }}
              >
                <Logo />
              </Box>
            </Box>
            <Divider
              sx={{
                mt: '7px',
                mx: theme.spacing(2)
              }}
            />
            <SidebarMenu />
          </Scrollbar>
          <Box
            style={{
              marginTop: "20px",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: "14px",
              fontWeight: "500",
              color: "#666",
              padding: "8px 20px",

            }}
          >
            Version {appVersion}
          </Box>
        </SidebarWrapper>
      </Drawer>
    </>
  );
}

export default Sidebar;
